import React, { useEffect } from 'react';
import * as actions from '../../store/actions';
import { Button, Dialog, DialogContent, makeStyles } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import { IIndexable } from '../GraphicsLibrary/GraphicDetail/GraphicDetail';
import { useTypedSelector } from '../../shared/hooks/useTypedSelector';
import KapinuaDialogTitle from '../KapinuaDialog/KapinuaDialogTitle';
import * as ValidateModel from '../../shared/ValidateModel';
import SnackBarType from '../../objects/app/snackbar';
import { useDispatch } from 'react-redux';
import {
    saveDesignName,
    saveDesignNameReset,
} from '../../store/actions/saveDesignAction';
import { useTranslation } from 'react-i18next';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    customerId: number | null;
    guestId: number | null;
}

const useStyles = makeStyles((theme) => ({
    dialog: {
        padding: '20px',
        textAlign: 'center',
    },
    button: {
        marginTop: '20px',
    },
    textInput: {
        height: '3rem',
        lineHeight: '3rem',
    },
}));

declare global {
    interface Window {
        GET_DESIGN_NAME_FCE_NAME: string;
        SET_DESIGN_NAME_FCE_NAME: string;
        GET_ID_DESIGN_FCE_NAME: string;
    }
}

const SaveDesignNameDialog: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const { isOpen, onClose, customerId, guestId } = props;
    const [name, setName] = React.useState<string>('');
    const [idDesign, setIdDesign] = React.useState<number | null>(null);

    const { loading, success } = useTypedSelector(
        (state) => state.saveDesign.saveDesignName
    );

    const messages = {
        saveDesignNameSuccess: t('Design name saved successfully.'),
    };

    useEffect(() => {
        if (isOpen) {
            try {
                ValidateModel.isWindowFce(
                    'GET_DESIGN_NAME_FCE_NAME',
                    window.GET_DESIGN_NAME_FCE_NAME
                );
                ValidateModel.isWindowFce(
                    'SET_DESIGN_NAME_FCE_NAME',
                    window.SET_DESIGN_NAME_FCE_NAME
                );
            } catch (error: any) {
                dispatch(
                    actions.addSnackbar(
                        t('Save design name dialog is not properly defined.') +
                            error.message,
                        SnackBarType.WARNING,
                        false
                    )
                );
            }
            setName((window as IIndexable)[window.GET_DESIGN_NAME_FCE_NAME]());
            setIdDesign(
                (window as IIndexable)[window.GET_ID_DESIGN_FCE_NAME]()
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
    };

    useEffect(() => {
        if (success && isOpen) {
            (window as IIndexable)[window.SET_DESIGN_NAME_FCE_NAME](name);
            onClose();
            dispatch(saveDesignNameReset());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    const handleSave = () => {
        if (idDesign !== null) {
            dispatch(
                saveDesignName(idDesign, customerId, guestId, name, messages)
            );
        }
    };

    return (
        <Dialog open={isOpen} maxWidth={'md'} onClose={onClose}>
            <KapinuaDialogTitle handleOnClose={onClose} centered={true}>
                <span>{t('Name your design')}</span>
            </KapinuaDialogTitle>
            <DialogContent className={classes.dialog}>
                <TextField
                    placeholder="Name"
                    value={name}
                    variant="outlined"
                    autoFocus={true}
                    onChange={handleNameChange}
                    InputProps={{
                        className: classes.textInput,
                        inputProps: {
                            style: { textAlign: 'center' },
                        },
                    }}
                    onKeyPress={(event) => {
                        if (event.key === 'Enter' && name.length > 0) {
                            handleSave();
                        }
                    }}
                />
                <br />
                <Button
                    onClick={handleSave}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    className={classes.button}
                    type="submit"
                    disabled={!name || name.length === 0 ? true : false}
                >
                    {!loading && <>{t('Save')}</>}
                    {loading && <CircularProgress size="1.5rem" />}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default SaveDesignNameDialog;
